/**
 * Wall Visualizer - Web Component
 * Two modes: (1) Interactive room mockups — mural always visible, click walls to switch, drag to pan.
 *            (2) Upload your own wall photo — mark 4 corners, drag to pan result.
 * Room foreground (furniture) renders on top of the mural for realism.
 */

/* ── Room scene definitions ─────────────────────────────────────────
   Each scene references a real photo (asset URL injected via the
   <wall-visualizer> data-room-* attributes).

   - `aspect` : width / height of the source photo, used to size the canvas.
   - `walls[]`: one or more wall regions. `poly` is a multi-vertex outline
                (fractional photo coords) in clockwise order. The renderer
                uses the bbox of the polygon as the bilinear-mapping quad and
                clips the mural to the polygon itself, so wallpaper only
                appears where the user actually drew on the annotated PDF.

   The polygons below were extracted automatically from the user's annotated
   PDF ("mockup walls.pdf"): for each page we detect the red/green/purple
   sketch pixels, dilate to close gaps in the hand-drawn line, fill the
   interior, trace the boundary, and simplify to ~10-25 vertices, then inset
   ~1.5% so the wallpaper sits inside the wall, not over the molding.

   Face-on walls (back walls) use the auto-traced polygon with a 3% centroid
   inset so the wallpaper sits inside the wall edges. Receding side walls
   (kids-corner left/right, bedroom right, powder-room right) carry an
   explicit `quad` trapezoid that runs ceiling-to-floor at the actual wall
   corners, so the mural tapers correctly with perspective. ─────────── */
const ROOM_SCENE_SPECS = [
  {
    id: 'minimal-living', name: 'Living Room', aspect: 1536 / 1024,
    walls: [
      { name: 'Back Wall', poly: [[0.8748, 0.6761], [0.1383, 0.6761], [0.1345, 0.6698], [0.1345, 0.0489], [0.1394, 0.0434], [0.8448, 0.0459], [0.8507, 0.0748], [0.8774, 0.0803], [0.8786, 0.6707]] }
    ]
  },
  {
    id: 'dining', name: 'Dining Room', aspect: 1536 / 1024,
    walls: [
      { name: 'Back Wall', poly: [[0.1801, 0.8633], [0.1566, 0.8633], [0.1475, 0.8506], [0.1514, 0.0982], [0.9485, 0.1039], [0.9411, 0.8605], [0.8132, 0.8597], [0.8200, 0.8304], [0.8009, 0.8150], [0.6871, 0.7992], [0.6759, 0.7791], [0.6448, 0.7660], [0.5915, 0.7686], [0.5662, 0.7957], [0.5093, 0.7966], [0.5022, 0.7799], [0.4799, 0.7703], [0.4242, 0.7625], [0.3925, 0.7756], [0.3773, 0.7966], [0.3244, 0.8001], [0.2522, 0.8281], [0.2499, 0.8579]] }
    ]
  },
  {
    id: 'kids-corner', name: 'Corner Kids Room', aspect: 1600 / 913,
    walls: [
      { name: 'Back Wall', poly: [[0.2702, 0.5691], [0.2658, 0.5645], [0.2691, 0.1424], [0.6960, 0.1480], [0.6945, 0.5681]] }
    ]
  },
  {
    id: 'kids-room', name: 'Kids Room', aspect: 1536 / 1024,
    walls: [
      { name: 'Back Wall', poly: [[0.4962, 0.8077], [0.2127, 0.8069], [0.1956, 0.7876], [0.1769, 0.7884], [0.1719, 0.7818], [0.1625, 0.7547], [0.1625, 0.0703], [0.1664, 0.0646], [0.8934, 0.0681], [0.8966, 0.6215], [0.8928, 0.6272], [0.8764, 0.6220], [0.8681, 0.6360], [0.8505, 0.6255], [0.8024, 0.7166], [0.7731, 0.7157], [0.7414, 0.6964], [0.6863, 0.7087], [0.5776, 0.7043], [0.5466, 0.7148], [0.5269, 0.7354]] }
    ]
  },
  {
    id: 'stair-foyer', name: 'Stair Foyer', aspect: 1402 / 1122,
    walls: [
      { name: 'Back Wall', poly: [[0.7116, 0.7591], [0.1582, 0.7530], [0.1575, 0.0476], [0.1625, 0.0405], [0.9881, 0.0409], [0.9881, 0.3254], [0.9825, 0.3234], [0.9600, 0.3448], [0.8662, 0.4499], [0.8599, 0.4403], [0.8672, 0.4263], [0.8629, 0.4209], [0.8319, 0.4209], [0.8223, 0.4272], [0.8295, 0.4462], [0.8249, 0.4957], [0.8269, 0.6339]] }
    ]
  },
  {
    id: 'luxe-bedroom', name: 'Bedroom', aspect: 1536 / 1024,
    walls: [
      { name: 'Back Wall',  poly: [[0.7871, 0.6214], [0.2150, 0.6214], [0.2087, 0.6113], [0.2082, 0.1162], [0.2132, 0.1096], [0.7918, 0.1105], [0.7956, 0.1197], [0.7915, 0.1258], [0.7915, 0.6148]] },
      // Right wall — TR pulled to the canvas top-right corner (y=0) so the
      // ceiling-line slope from corner down to far edge is fully visible.
      { name: 'Right Wall', poly: [[0.8604, 0.0400], [1.0000, 0.0000], [0.9992, 0.8391], [0.8604, 0.7435]],
        quad: [[0.8604, 0.0400], [1.0000, 0.0000], [0.9992, 0.8391], [0.8604, 0.7435]] }
    ]
  },
  {
    id: 'entryway', name: 'Entryway', aspect: 1409 / 1116,
    walls: [
      // Auto-traced outline (with table notch) so wallpaper does NOT cover the console table.
      { name: 'Back Wall', poly: [[0.2797, 0.7355], [0.1294, 0.7355], [0.1179, 0.7276], [0.1166, 0.0355], [0.1222, 0.0268], [0.8824, 0.0277], [0.8912, 0.0330], [0.8866, 0.0438], [0.8830, 0.7322], [0.6530, 0.7297], [0.6506, 0.5688], [0.6571, 0.5606], [0.6530, 0.5544], [0.3012, 0.5536], [0.2918, 0.5598], [0.2977, 0.5680], [0.2983, 0.7276]] }
    ]
  },
  {
    id: 'powder-room', name: 'Powder Room', aspect: 1122 / 1402,
    walls: [
      { name: 'Back Wall',  poly: [[0.8175, 0.7838], [0.4089, 0.7830], [0.4022, 0.7776], [0.4094, 0.7685], [0.4126, 0.7255], [0.4074, 0.5808], [0.4006, 0.5754], [0.3490, 0.5771], [0.3414, 0.5709], [0.3423, 0.5362], [0.3336, 0.5241], [0.3016, 0.5250], [0.2892, 0.5150], [0.2758, 0.5341], [0.2619, 0.5279], [0.2619, 0.1268], [0.2686, 0.0983], [0.8175, 0.0966], [0.8304, 0.1053], [0.8242, 0.1111], [0.8264, 0.7759]] },
      // Right wall — top-right pulled back down to the actual ceiling line
      // (was overshooting the top of the frame). Bottom-right still past the
      // frame to keep the floor-line slope.
      { name: 'Right Wall', poly: [[0.8343, 0.0590], [0.9524, 0.0050], [0.9524, 0.9688], [0.8334, 0.8039]],
        quad: [[0.8343, 0.0590], [0.9524, 0.0050], [0.9524, 0.9688], [0.8334, 0.8039]] }
    ]
  },
  {
    id: 'kitchen', name: 'Kitchen', aspect: 1536 / 1024,
    walls: [
      { name: 'Back Wall', poly: [[0.2332, 0.8308], [0.2276, 0.8251], [0.2282, 0.6323], [0.1529, 0.6213], [0.1432, 0.6131], [0.1432, 0.0882], [0.1487, 0.0799], [0.9644, 0.0781], [0.9682, 0.0838], [0.9649, 0.7388], [0.7414, 0.7388], [0.7367, 0.7186], [0.7032, 0.7064], [0.6932, 0.6880], [0.6833, 0.6845], [0.6739, 0.6862], [0.6651, 0.7047], [0.6351, 0.7082], [0.6190, 0.7244], [0.6225, 0.7322], [0.6164, 0.7397], [0.4644, 0.7406], [0.4459, 0.8172], [0.4386, 0.8255], [0.2843, 0.8159], [0.2391, 0.8229]] }
    ]
  }
];

/* Turn a scene spec into the same shape consumed by the renderer. */
function makeRoomScene(spec, imageUrl, onLoaded) {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  let loaded = false;
  img.onload = () => { loaded = true; if (onLoaded) onLoaded(); };
  if (imageUrl) img.src = imageUrl;

  const polyToPx = (poly, w, h) => poly.map(([x, y]) => ({ x: x * w, y: y * h }));

  return {
    id: spec.id,
    name: spec.name,
    aspect: spec.aspect,
    image: img,
    isLoaded: () => loaded,
    drawBackground(ctx, w, h) {
      if (loaded) {
        ctx.drawImage(img, 0, 0, w, h);
      } else {
        ctx.fillStyle = '#efe9e0';
        ctx.fillRect(0, 0, w, h);
        ctx.fillStyle = '#b8a892';
        ctx.font = '500 14px Jost, sans-serif';
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText('Loading room…', w / 2, h / 2);
      }
    },
    drawForeground(ctx, w, h) {
      if (!loaded || !spec.foreground) return;
      spec.foreground.forEach(poly => {
        if (!poly || poly.length < 3) return;
        const verts = polyToPx(poly, w, h);
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(verts[0].x, verts[0].y);
        for (let i = 1; i < verts.length; i++) ctx.lineTo(verts[i].x, verts[i].y);
        ctx.closePath();
        ctx.clip();
        ctx.drawImage(img, 0, 0, w, h);
        ctx.restore();
      });
    },
    walls: spec.walls.map(w => {
      // The bilinear mapping quad: either the explicit `quad` (for trapezoidal
      // receding walls) or the axis-aligned bbox of the outline.
      const quadFrac = w.quad || (() => {
        const xs = w.poly.map(p => p[0]), ys = w.poly.map(p => p[1]);
        const x0 = Math.min(...xs), x1 = Math.max(...xs);
        const y0 = Math.min(...ys), y1 = Math.max(...ys);
        return [[x0, y0], [x1, y0], [x1, y1], [x0, y1]];
      })();
      return {
        name: w.name,
        corners(cw, ch) { return polyToPx(w.poly, cw, ch); },
        quad(cw, ch) { return polyToPx(quadFrac, cw, ch); }
      };
    })
  };
}


/* ── Component ──────────────────────────────────────────────────── */
class WallVisualizer extends HTMLElement {
  constructor() {
    super();
    this.corners = [];
    this.wallPhoto = null;
    this.productImage = null;
    this.opacity = 0.85;
    this.mode = null;
    this.panU = 0;
    this.panV = 0;
    this.isDragging = false;
    this.selectedRoom = null;
    this.activeWallIdx = 0;
    this.hoveredWallIdx = -1;
    this.stepFlow = [];
    this.currentStepIdx = 0;
    this.exclusions = [];
    this.exclusionInProgress = null;
    this.markStage = 'wall';
    this.roomScenes = [];
  }

  connectedCallback() {
    this.productImageUrl = this.dataset.productImage;
    this.productTitle = this.dataset.productTitle;
    this.buildRoomScenes();
    this.bindElements();
    this.buildImagePicker();
    this.bindEvents();
    this.loadProductImage();
  }

  /* Build scene instances using asset URLs injected on data-room-* attributes. */
  buildRoomScenes() {
    const dataAttr = (id) => this.dataset[
      'room' + id.replace(/(^|-)([a-z])/g, (_, __, c) => c.toUpperCase())
    ];
    this.roomScenes = ROOM_SCENE_SPECS
      .map(spec => {
        const url = dataAttr(spec.id);
        if (!url) return null;
        let scene;
        scene = makeRoomScene(spec, url, () => {
          if (this.mode === 'mockup' && this.selectedRoom === scene) this.rerenderRoom();
        });
        return scene;
      })
      .filter(Boolean);
  }

  /* ── Image picker ───────────────────────────────────────────── */
  buildImagePicker() {
    const dataEl = this.querySelector('.wall-visualizer__media-json');
    if (!dataEl) return;
    let images;
    try { images = JSON.parse(dataEl.textContent); } catch (e) { return; }
    this.variantImages = images;

    this.querySelectorAll('.wall-visualizer__image-picker').forEach(pickerEl => {
      if (images.length <= 1) return;
      const label = document.createElement('p');
      label.className = 'wv-picker-label';
      label.textContent = 'Pattern image:';
      const strip = document.createElement('div');
      strip.className = 'wv-picker-strip';
      const activeUrl = this.productImageUrl.split('?')[0];

      images.forEach((img, i) => {
        const isActive = img.url.split('?')[0] === activeUrl;
        const thumb = document.createElement('button');
        thumb.type = 'button';
        thumb.className = 'wv-picker-thumb' + (isActive ? ' active' : '');
        thumb.dataset.variantId = img.variantId || '';
        thumb.innerHTML = `<img src="${img.thumb}" alt="Image ${i + 1}" loading="lazy">`;
        thumb.addEventListener('click', () => {
          this.querySelectorAll('.wv-picker-thumb').forEach(t => {
            t.classList.toggle('active', t.dataset.variantId === thumb.dataset.variantId);
          });
          this.productImageUrl = img.url;
          this.loadProductImage(() => {
            if (this.mode === 'mockup') this.rerenderRoom();
            else if (this.stepFlow[this.currentStepIdx] === 'result') this.rerenderUpload();
          });
        });
        strip.appendChild(thumb);
      });
      pickerEl.appendChild(label);
      pickerEl.appendChild(strip);
    });
    this.listenForVariantChanges();
  }

  listenForVariantChanges() {
    if (!this.variantImages || this.variantImages.length < 2) return;
    const form = document.querySelector('form[id*="ProductForm"]');
    if (!form) return;
    form.addEventListener('change', () => {
      setTimeout(() => {
        const idInput = form.querySelector('input[name="id"]');
        if (!idInput) return;
        const vid = parseInt(idInput.value);
        const match = this.variantImages.find(v => v.variantId === vid);
        if (match && match.url !== this.productImageUrl) {
          this.productImageUrl = match.url;
          this.loadProductImage(() => {
            if (this.mode === 'mockup') this.rerenderRoom();
          });
          this.querySelectorAll('.wv-picker-thumb').forEach(t => {
            t.classList.toggle('active', t.dataset.variantId == vid);
          });
        }
      }, 100);
    });
  }

  /* ── Element refs & events ──────────────────────────────────── */
  bindElements() {
    this.trigger = this.querySelector('.wall-visualizer__trigger');
    this.modal = this.querySelector('.wall-visualizer__modal');
    this.closeBtn = this.querySelector('.wall-visualizer__close');
    this.stepsContainer = this.querySelector('.wall-visualizer__steps-indicator');
    this.uploadArea = this.querySelector('.wall-visualizer__upload-area');
    this.uploadInput = this.querySelector('.wall-visualizer__upload-input');
    this.markCanvas = this.querySelector('.wall-visualizer__canvas');
    this.canvasWrap = this.querySelector('.wall-visualizer__canvas-wrap');
    this.resultCanvas = this.querySelector('.wall-visualizer__result-canvas');
    this.undoBtn = this.querySelector('[data-action="undo"]');
    this.resetBtn = this.querySelector('[data-action="reset"]');
    this.applyBtn = this.querySelector('[data-action="apply"]');
    this.downloadBtn = this.querySelector('[data-action="download"]');
    this.retryBtn = this.querySelector('[data-action="retry"]');
    this.roomCanvas = this.querySelector('.wall-visualizer__room-canvas');
    this.roomCanvasWrap = this.querySelector('.wall-visualizer__room-canvas-wrap');
    this.roomSelect = this.querySelector('.wall-visualizer__room-select');
    this.wallBtnsContainer = this.querySelector('.wall-visualizer__wall-buttons');
    this.downloadRoomBtn = this.querySelector('[data-action="download-room"]');
    this.backToModeBtn = this.querySelector('[data-action="back-to-mode"]');
    this.backFromUploadBtn = this.querySelector('[data-action="back-from-upload"]');
    this.backToUploadBtn = this.querySelector('[data-action="back-to-upload"]');
    this.addObjectBtn = this.querySelector('[data-action="add-object"]');
    this.doneObjectBtn = this.querySelector('[data-action="done-object"]');
    this.cancelObjectBtn = this.querySelector('[data-action="cancel-object"]');
    this.markStepDesc = this.querySelector('[data-step="mark"] .wall-visualizer__step-desc');
    this.wallStageActions = this.querySelectorAll('.wv-action-wall');
    this.exclusionStageActions = this.querySelectorAll('.wv-action-exclusion');
    this.helpBtn = this.querySelector('.wall-visualizer__help');
    this.tutorial = this.querySelector('.wall-visualizer__tutorial');
    this.tutCloseBtn = this.querySelector('.wv-tut-close');
    this.tutDoneBtn = this.querySelector('.wv-tut-done');
  }

  bindEvents() {
    // Modal
    this.trigger.addEventListener('click', () => this.openModal());
    this.closeBtn.addEventListener('click', () => this.closeModal());
    this.modal.addEventListener('click', (e) => { if (e.target === this.modal) this.closeModal(); });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('open')) this.closeModal();
    });

    // Mode selection
    this.querySelectorAll('[data-mode]').forEach(btn => {
      btn.addEventListener('click', () => this.selectMode(btn.dataset.mode));
    });

    // Upload
    this.uploadArea.addEventListener('click', () => this.uploadInput.click());
    this.uploadInput.addEventListener('change', (e) => this.handleUpload(e.target.files[0]));
    this.uploadArea.addEventListener('dragover', (e) => { e.preventDefault(); this.uploadArea.classList.add('dragover'); });
    this.uploadArea.addEventListener('dragleave', () => this.uploadArea.classList.remove('dragover'));
    this.uploadArea.addEventListener('drop', (e) => {
      e.preventDefault(); this.uploadArea.classList.remove('dragover');
      if (e.dataTransfer.files[0]) this.handleUpload(e.dataTransfer.files[0]);
    });

    // Corner marking
    this.markCanvas.addEventListener('click', (e) => this.addCorner(e));
    this.undoBtn.addEventListener('click', () => this.undoCorner());
    this.resetBtn.addEventListener('click', () => this.resetCorners());
    this.applyBtn.addEventListener('click', () => this.applyUploadVisualization());
    this.downloadBtn.addEventListener('click', () => this.downloadCanvas(this.resultCanvas));
    this.retryBtn.addEventListener('click', () => { this.panU = 0; this.panV = 0; this.goToStepByName('mark'); });

    // Reset wallpaper pan position
    this.querySelectorAll('[data-pan="reset"]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.panU = 0; this.panV = 0;
        if (this.mode === 'mockup') this.rerenderRoom();
        else this.rerenderUpload();
      });
    });

    // Room canvas: hover + pointer for click/drag
    this.roomCanvas.addEventListener('mousemove', (e) => this.handleRoomHover(e));
    this.roomCanvas.addEventListener('mouseleave', () => {
      if (this.hoveredWallIdx !== -1) { this.hoveredWallIdx = -1; this.rerenderRoom(); }
      this.roomCanvas.style.cursor = 'grab';
    });
    this.roomCanvas.addEventListener('pointerdown', (e) => this.onRoomPointerDown(e));
    this.roomCanvas.addEventListener('pointermove', (e) => this.onRoomPointerMove(e));
    this.roomCanvas.addEventListener('pointerup', (e) => this.onRoomPointerUp(e));
    this.roomCanvas.addEventListener('pointerleave', () => { this.isDragging = false; });

    // Result canvas drag (upload mode)
    this.resultCanvas.addEventListener('pointerdown', (e) => this.onResultDragStart(e));
    this.resultCanvas.addEventListener('pointermove', (e) => this.onResultDragMove(e));
    this.resultCanvas.addEventListener('pointerup', () => this.onResultDragEnd());
    this.resultCanvas.addEventListener('pointerleave', () => this.onResultDragEnd());

    // Room view buttons
    this.downloadRoomBtn.addEventListener('click', () => this.downloadCanvas(this.roomCanvas));
    this.backToModeBtn.addEventListener('click', () => { this.mode = null; this.goToStepByName('mode-select'); });

    // Back navigation + exclusion controls
    if (this.backFromUploadBtn) this.backFromUploadBtn.addEventListener('click', () => {
      this.mode = null;
      this.wallPhoto = null;
      this.corners = [];
      this.exclusions = [];
      this.exclusionInProgress = null;
      this.markStage = 'wall';
      this.uploadInput.value = '';
      this.goToStepByName('mode-select');
    });
    if (this.backToUploadBtn) this.backToUploadBtn.addEventListener('click', () => {
      this.corners = [];
      this.exclusions = [];
      this.exclusionInProgress = null;
      this.markStage = 'wall';
      this.uploadInput.value = '';
      this.clearCornerMarkers();
      this.goToStepByName('upload');
    });
    if (this.addObjectBtn) this.addObjectBtn.addEventListener('click', () => this.startExclusion());
    if (this.doneObjectBtn) this.doneObjectBtn.addEventListener('click', () => this.finishExclusion());
    if (this.cancelObjectBtn) this.cancelObjectBtn.addEventListener('click', () => this.cancelExclusion());

    // Tutorial overlay
    if (this.helpBtn) this.helpBtn.addEventListener('click', () => this.openTutorial());
    if (this.tutCloseBtn) this.tutCloseBtn.addEventListener('click', () => this.closeTutorial());
    if (this.tutDoneBtn) this.tutDoneBtn.addEventListener('click', () => this.closeTutorial());
  }

  openTutorial() { if (this.tutorial) this.tutorial.hidden = false; }
  closeTutorial() { if (this.tutorial) this.tutorial.hidden = true; }

  /* ── Product image ──────────────────────────────────────────── */
  loadProductImage(callback) {
    if (!this.productImageUrl) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => { this.productImage = img; if (callback) callback(); };
    img.src = this.productImageUrl;
  }

  /* Switch the visualised design (used by the Show On My Wall studio page). */
  setProduct(imageUrl, title) {
    if (!imageUrl) return;
    this.productImageUrl = imageUrl;
    if (title) this.dataset.productTitle = title;
    this.loadProductImage(() => {
      if (this.mode === 'mockup') this.rerenderRoom();
      else if (this.stepFlow && this.stepFlow[this.currentStepIdx] === 'result') this.rerenderUpload();
    });
  }

  /* ── Modal ──────────────────────────────────────────────────── */
  openModal() {
    this.modal.classList.add('open');
    document.body.style.overflow = 'hidden';
    if (!this.mode) this.goToStepByName('mode-select');
  }

  closeModal() {
    this.modal.classList.remove('open');
    document.body.style.overflow = '';
    // Reset visualizer state so next open starts at mode selection
    this.mode = null;
    this.wallPhoto = null;
    this.corners = [];
    this.exclusions = [];
    this.exclusionInProgress = null;
    this.markStage = 'wall';
    this.panU = 0; this.panV = 0;
    if (this.uploadInput) this.uploadInput.value = '';
    this.clearCornerMarkers();
    this.closeTutorial();
  }

  /* ── Mode selection ─────────────────────────────────────────── */
  selectMode(mode) {
    this.mode = mode;
    if (mode === 'upload') {
      this.stepFlow = ['mode-select', 'upload', 'mark', 'result'];
      this.goToStepByName('upload');
    } else {
      this.stepFlow = ['mode-select', 'room-view'];
      this.activeWallIdx = 0;
      this.panU = 0; this.panV = 0;
      this.initRoomView();
      this.goToStepByName('room-view');
    }
  }

  /* ── Step navigation ────────────────────────────────────────── */
  goToStepByName(name) {
    if (!this.stepFlow.length) this.stepFlow = ['mode-select'];
    this.currentStepIdx = Math.max(0, this.stepFlow.indexOf(name));
    this.querySelectorAll('.wall-visualizer__step').forEach(s => {
      s.classList.toggle('active', s.dataset.step === name);
    });
    this.stepsContainer.innerHTML = '';
    this.stepFlow.forEach((_, i) => {
      const dot = document.createElement('div');
      dot.className = 'wall-visualizer__step-dot';
      if (i === this.currentStepIdx) dot.classList.add('active');
      else if (i < this.currentStepIdx) dot.classList.add('completed');
      this.stepsContainer.appendChild(dot);
    });
    if (name === 'mark') {
      this.exclusions = [];
      this.exclusionInProgress = null;
      this.markStage = 'wall';
      this.clearCornerMarkers();
      if (this.wallPhoto) {
        this.drawWallPhoto();
        this.corners = this.defaultCorners();
        this.redrawAllMarkers();
        this.drawConnectingLines();
      } else {
        this.corners = [];
      }
      this.updateMarkingUI();
    }
  }

  defaultCorners() {
    const cw = this.markCanvas.width, ch = this.markCanvas.height;
    const ix = cw * 0.15, iy = ch * 0.15;
    return [
      { x: ix, y: iy },
      { x: cw - ix, y: iy },
      { x: cw - ix, y: ch - iy },
      { x: ix, y: ch - iy }
    ];
  }

  /* ── Room mockup: interactive view ──────────────────────────── */
  initRoomView() {
    // Room dropdown
    if (!this.roomSelect) return;
    this.roomSelect.innerHTML = '';
    this.roomScenes.forEach((room, i) => {
      const opt = document.createElement('option');
      opt.value = String(i);
      opt.textContent = room.name;
      this.roomSelect.appendChild(opt);
    });
    // Bind once
    if (!this._roomSelectBound) {
      this.roomSelect.addEventListener('change', (e) => {
        const idx = parseInt(e.target.value, 10) || 0;
        this.selectRoom(idx);
      });
      this._roomSelectBound = true;
    }
    if (this.roomScenes.length) {
      this.roomSelect.value = '0';
      this.selectRoom(0);
    }
  }

  selectRoom(idx) {
    this.selectedRoom = this.roomScenes[idx];
    if (!this.selectedRoom) return;
    // Default to the first wall; if there's more than one (e.g. corner rooms),
    // wall-buttons let the user switch.
    this.activeWallIdx = 0;
    this.hoveredWallIdx = -1;
    this.panU = 0; this.panV = 0;

    // Size the canvas to match the room photo's aspect ratio (capped at 900px wide).
    const cw = 900;
    const ch = Math.round(cw / (this.selectedRoom.aspect || (3 / 2)));
    this.roomCanvas.width = cw;
    this.roomCanvas.height = ch;
    this.buildWallButtons();
    this.rerenderRoom();
  }

  buildWallButtons() {
    this.wallBtnsContainer.innerHTML = '';
    // Hide the wall switcher entirely on single-wall scenes — there's nothing to switch.
    // (Author CSS sets display:flex, so `hidden` attribute alone is overridden.)
    this.wallBtnsContainer.style.display = this.selectedRoom.walls.length < 2 ? 'none' : '';
    this.selectedRoom.walls.forEach((wall, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'wall-visualizer__wall-btn' + (i === this.activeWallIdx ? ' active' : '');
      btn.textContent = wall.name;
      btn.addEventListener('click', () => this.switchWall(i));
      this.wallBtnsContainer.appendChild(btn);
    });
  }

  switchWall(idx) {
    if (idx === this.activeWallIdx) return;
    this.activeWallIdx = idx;
    this.panU = 0; this.panV = 0;
    this.wallBtnsContainer.querySelectorAll('.wall-visualizer__wall-btn').forEach((b, i) => {
      b.classList.toggle('active', i === idx);
    });
    this.rerenderRoom();
  }

  rerenderRoom() {
    if (!this.selectedRoom) return;
    const ctx = this.roomCanvas.getContext('2d');
    const w = this.roomCanvas.width, h = this.roomCanvas.height;
    ctx.clearRect(0, 0, w, h);

    // 1. Background (walls, floor, ceiling)
    this.selectedRoom.drawBackground(ctx, w, h);

    // 2. Mural on active wall — corners() returns the full outline polygon;
    //    quad() returns the 4-corner bbox/perspective quad used for bilinear mapping.
    if (this.productImage && this.activeWallIdx >= 0) {
      const wall = this.selectedRoom.walls[this.activeWallIdx];
      this.clipPoly = wall.corners(w, h);
      this.corners = wall.quad(w, h);
      this.renderPerspective(ctx);
      this.clipPoly = null;
    }

    // 3. Foreground (furniture — appears IN FRONT of mural)
    this.selectedRoom.drawForeground(ctx, w, h);

    // 4. Hover highlight on non-active walls
    if (this.hoveredWallIdx >= 0 && this.hoveredWallIdx !== this.activeWallIdx) {
      const hCorners = this.selectedRoom.walls[this.hoveredWallIdx].corners(w, h);
      ctx.fillStyle = 'rgba(123, 84, 84, 0.1)';
      ctx.beginPath();
      ctx.moveTo(hCorners[0].x, hCorners[0].y);
      for (let j = 1; j < hCorners.length; j++) ctx.lineTo(hCorners[j].x, hCorners[j].y);
      ctx.closePath(); ctx.fill();
      ctx.strokeStyle = 'rgba(123, 84, 84, 0.4)';
      ctx.lineWidth = 2; ctx.setLineDash([6, 4]); ctx.stroke(); ctx.setLineDash([]);
    }

    // 5. Wall labels on non-active walls
    this.selectedRoom.walls.forEach((wall, i) => {
      if (i === this.activeWallIdx) return;
      const corners = wall.corners(w, h);
      const cx = corners.reduce((s, c) => s + c.x, 0) / corners.length;
      const cy = corners.reduce((s, c) => s + c.y, 0) / corners.length;
      ctx.save();
      ctx.font = '500 12px Jost, sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      const tw = ctx.measureText(wall.name).width + 14;
      ctx.fillStyle = i === this.hoveredWallIdx ? 'rgba(123,84,84,0.7)' : 'rgba(0,0,0,0.25)';
      ctx.beginPath(); ctx.roundRect(cx - tw / 2, cy - 10, tw, 20, 4); ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.fillText(wall.name, cx, cy + 1);
      ctx.restore();
    });
  }

  handleRoomHover(e) {
    if (this.isDragging || !this.selectedRoom) return;
    const rect = this.roomCanvas.getBoundingClientRect();
    const sx = this.roomCanvas.width / rect.width;
    const sy = this.roomCanvas.height / rect.height;
    const mx = (e.clientX - rect.left) * sx;
    const my = (e.clientY - rect.top) * sy;
    const w = this.roomCanvas.width, h = this.roomCanvas.height;

    let hit = -1;
    this.selectedRoom.walls.forEach((wall, i) => {
      if (this.pointInPolygon(mx, my, wall.corners(w, h))) hit = i;
    });

    if (hit !== this.hoveredWallIdx) {
      this.hoveredWallIdx = hit;
      this.rerenderRoom();
    }
    this.roomCanvas.style.cursor = (hit >= 0 && hit !== this.activeWallIdx) ? 'pointer' : 'grab';
  }

  onRoomPointerDown(e) {
    this.dragStartX = e.clientX;
    this.dragStartY = e.clientY;
    this.dragStartPanU = this.panU;
    this.dragStartPanV = this.panV;
    this.dragMoved = false;
    this.roomCanvas.setPointerCapture(e.pointerId);
  }

  onRoomPointerMove(e) {
    if (this.dragStartX == null) return;
    const dx = e.clientX - this.dragStartX;
    const dy = e.clientY - this.dragStartY;
    if (!this.dragMoved && Math.hypot(dx, dy) < 5) return;
    this.dragMoved = true;
    this.isDragging = true;
    this.roomCanvas.style.cursor = 'grabbing';

    const rect = this.roomCanvas.getBoundingClientRect();
    this.panU = this.dragStartPanU - dx / rect.width;
    this.panV = this.dragStartPanV - dy / rect.height;
    this.rerenderRoom();
  }

  onRoomPointerUp(e) {
    if (!this.dragMoved) {
      // It was a click — switch wall if clicked on a different one
      const rect = this.roomCanvas.getBoundingClientRect();
      const sx = this.roomCanvas.width / rect.width;
      const sy = this.roomCanvas.height / rect.height;
      const mx = (e.clientX - rect.left) * sx;
      const my = (e.clientY - rect.top) * sy;
      const w = this.roomCanvas.width, h = this.roomCanvas.height;

      this.selectedRoom.walls.forEach((wall, i) => {
        if (i !== this.activeWallIdx && this.pointInPolygon(mx, my, wall.corners(w, h))) {
          this.switchWall(i);
        }
      });
    }
    this.dragStartX = null;
    this.isDragging = false;
    this.roomCanvas.style.cursor = 'grab';
  }

  pointInPolygon(px, py, verts) {
    let inside = false;
    for (let i = 0, j = verts.length - 1; i < verts.length; j = i++) {
      const xi = verts[i].x, yi = verts[i].y, xj = verts[j].x, yj = verts[j].y;
      if ((yi > py) !== (yj > py) && px < (xj - xi) * (py - yi) / (yj - yi) + xi) inside = !inside;
    }
    return inside;
  }

  /* ── Upload mode ────────────────────────────────────────────── */
  handleUpload(file) {
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => { this.wallPhoto = img; this.drawWallPhoto(); this.goToStepByName('mark'); };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  }

  drawWallPhoto() {
    const canvas = this.markCanvas, ctx = canvas.getContext('2d');
    const maxW = 900, maxH = window.innerHeight * 0.6;
    let w = this.wallPhoto.naturalWidth, h = this.wallPhoto.naturalHeight;
    if (w > maxW) { h = h * (maxW / w); w = maxW; }
    if (h > maxH) { w = w * (maxH / h); h = maxH; }
    canvas.width = w; canvas.height = h;
    ctx.drawImage(this.wallPhoto, 0, 0, w, h);
  }

  addCorner(e) {
    if (this.markStage !== 'exclusion' || !this.exclusionInProgress) return;
    const rect = this.markCanvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (this.markCanvas.width / rect.width);
    const y = (e.clientY - rect.top) * (this.markCanvas.height / rect.height);
    this.exclusionInProgress.push({ x, y });
    this.redrawAllMarkers();
    this.drawConnectingLines();
    this.updateMarkingUI();
  }

  drawEdgeHandle(idx) {
    const handle = document.createElement('div');
    handle.className = 'wall-visualizer__edge';
    const grip = document.createElement('span');
    grip.className = 'wall-visualizer__edge-grip';
    handle.appendChild(grip);

    let dragActive = false;
    let dragStartX = 0, dragStartY = 0;
    let startA = null, startB = null;

    handle.addEventListener('pointerdown', (e) => {
      if (this.markStage === 'exclusion') { this.addCorner(e); return; }
      e.preventDefault();
      e.stopPropagation();
      handle.setPointerCapture(e.pointerId);
      dragActive = true;
      dragStartX = e.clientX;
      dragStartY = e.clientY;
      startA = { ...this.corners[idx] };
      startB = { ...this.corners[(idx + 1) % 4] };
    });
    handle.addEventListener('pointermove', (e) => {
      if (!dragActive) return;
      const cRect = this.markCanvas.getBoundingClientRect();
      const scaleX = this.markCanvas.width / cRect.width;
      const scaleY = this.markCanvas.height / cRect.height;
      const dx = (e.clientX - dragStartX) * scaleX;
      const dy = (e.clientY - dragStartY) * scaleY;
      const w = this.markCanvas.width, h = this.markCanvas.height;
      this.corners[idx] = {
        x: Math.max(0, Math.min(w, startA.x + dx)),
        y: Math.max(0, Math.min(h, startA.y + dy))
      };
      this.corners[(idx + 1) % 4] = {
        x: Math.max(0, Math.min(w, startB.x + dx)),
        y: Math.max(0, Math.min(h, startB.y + dy))
      };
      this.repositionEdgeHandles();
      this.drawConnectingLines();
    });
    const endDrag = (e) => {
      if (dragActive) {
        dragActive = false;
        try { handle.releasePointerCapture(e.pointerId); } catch (_) {}
      }
    };
    handle.addEventListener('pointerup', endDrag);
    handle.addEventListener('pointercancel', endDrag);

    this.canvasWrap.appendChild(handle);
    this.positionEdgeHandle(handle, idx);
    return handle;
  }

  positionEdgeHandle(handle, idx) {
    const a = this.corners[idx];
    const b = this.corners[(idx + 1) % 4];
    const cRect = this.markCanvas.getBoundingClientRect();
    const sx = cRect.width / this.markCanvas.width;
    const sy = cRect.height / this.markCanvas.height;
    const ax = a.x * sx, ay = a.y * sy;
    const bx = b.x * sx, by = b.y * sy;
    const midX = (ax + bx) / 2, midY = (ay + by) / 2;
    const len = Math.hypot(bx - ax, by - ay);
    const angle = Math.atan2(by - ay, bx - ax);
    handle.style.left = midX + 'px';
    handle.style.top = midY + 'px';
    handle.style.width = len + 'px';
    handle.style.transform = `translate(-50%, -50%) rotate(${angle}rad)`;
  }

  repositionEdgeHandles() {
    if (!this.edgeHandles) return;
    this.edgeHandles.forEach((handle, i) => this.positionEdgeHandle(handle, i));
  }

  drawExclusionMarker(x, y, pending, num) {
    const rect = this.markCanvas.getBoundingClientRect();
    const marker = document.createElement('div');
    marker.className = 'wall-visualizer__corner wall-visualizer__corner--exclusion' + (pending ? ' wall-visualizer__corner--exclusion-pending' : '');
    marker.style.left = (x * rect.width / this.markCanvas.width) + 'px';
    marker.style.top = (y * rect.height / this.markCanvas.height) + 'px';
    if (pending && num) {
      const label = document.createElement('span');
      label.className = 'wall-visualizer__excl-label';
      label.textContent = num;
      marker.appendChild(label);
    }
    this.canvasWrap.appendChild(marker);
  }

  clearCornerMarkers() {
    this.canvasWrap.querySelectorAll('.wall-visualizer__corner, .wall-visualizer__edge').forEach(m => m.remove());
    this.edgeHandles = [];
  }

  redrawAllMarkers() {
    this.clearCornerMarkers();
    if (this.corners.length === 4 && this.markStage !== 'exclusion') {
      this.edgeHandles = [];
      for (let i = 0; i < 4; i++) this.edgeHandles.push(this.drawEdgeHandle(i));
    }
    this.exclusions.forEach(poly => {
      poly.forEach(p => this.drawExclusionMarker(p.x, p.y, false));
    });
    if (this.exclusionInProgress) {
      this.exclusionInProgress.forEach((p, i) => this.drawExclusionMarker(p.x, p.y, true, i + 1));
    }
  }

  drawConnectingLines() {
    const canvas = this.markCanvas, ctx = canvas.getContext('2d');
    ctx.drawImage(this.wallPhoto, 0, 0, canvas.width, canvas.height);

    // Wall polygon outline
    if (this.corners.length >= 2) {
      ctx.strokeStyle = '#7b5454'; ctx.lineWidth = 2; ctx.setLineDash([6, 4]);
      ctx.beginPath(); ctx.moveTo(this.corners[0].x, this.corners[0].y);
      for (let i = 1; i < this.corners.length; i++) ctx.lineTo(this.corners[i].x, this.corners[i].y);
      if (this.corners.length === 4) ctx.closePath();
      ctx.stroke();
    }
    ctx.setLineDash([]);

    // Completed exclusion polygons (filled sage green)
    this.exclusions.forEach(poly => {
      if (poly.length < 3) return;
      ctx.beginPath();
      ctx.moveTo(poly[0].x, poly[0].y);
      for (let i = 1; i < poly.length; i++) ctx.lineTo(poly[i].x, poly[i].y);
      ctx.closePath();
      ctx.fillStyle = 'rgba(107, 142, 78, 0.28)';
      ctx.fill();
      ctx.strokeStyle = '#6b8e4e';
      ctx.lineWidth = 1.5;
      ctx.stroke();
    });

    // In-progress exclusion outline (dashed)
    if (this.exclusionInProgress && this.exclusionInProgress.length > 0) {
      const poly = this.exclusionInProgress;
      // White underlay for contrast on busy photos
      ctx.strokeStyle = 'rgba(255,255,255,0.85)';
      ctx.lineWidth = 4.5;
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(poly[0].x, poly[0].y);
      for (let i = 1; i < poly.length; i++) ctx.lineTo(poly[i].x, poly[i].y);
      if (poly.length >= 3) ctx.closePath();
      ctx.stroke();
      // Orange dashed line on top
      ctx.strokeStyle = '#ee7a2a';
      ctx.lineWidth = 2.5;
      ctx.setLineDash([6, 4]);
      ctx.beginPath();
      ctx.moveTo(poly[0].x, poly[0].y);
      for (let i = 1; i < poly.length; i++) ctx.lineTo(poly[i].x, poly[i].y);
      if (poly.length >= 3) ctx.closePath();
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }

  startExclusion() {
    if (this.corners.length < 4) return;
    this.markStage = 'exclusion';
    this.exclusionInProgress = [];
    this.redrawAllMarkers();
    this.updateMarkingUI();
  }

  finishExclusion() {
    if (!this.exclusionInProgress || this.exclusionInProgress.length < 3) return;
    this.exclusions.push(this.exclusionInProgress);
    this.exclusionInProgress = null;
    this.markStage = 'wall';
    this.redrawAllMarkers();
    this.drawConnectingLines();
    this.updateMarkingUI();
  }

  cancelExclusion() {
    this.exclusionInProgress = null;
    this.markStage = 'wall';
    this.redrawAllMarkers();
    this.drawConnectingLines();
    this.updateMarkingUI();
  }

  undoCorner() {
    // Undo in-progress exclusion point
    if (this.markStage === 'exclusion' && this.exclusionInProgress && this.exclusionInProgress.length) {
      this.exclusionInProgress.pop();
      this.redrawAllMarkers();
      this.drawConnectingLines();
      this.updateMarkingUI();
      return;
    }
    // Undo last completed exclusion
    if (this.exclusions.length) {
      this.exclusions.pop();
      this.redrawAllMarkers();
      this.drawConnectingLines();
      this.updateMarkingUI();
    }
  }

  resetCorners() {
    this.exclusions = [];
    this.exclusionInProgress = null;
    this.markStage = 'wall';
    if (this.wallPhoto) {
      this.corners = this.defaultCorners();
    } else {
      this.corners = [];
    }
    this.clearCornerMarkers();
    this.drawWallPhoto();
    this.redrawAllMarkers();
    this.drawConnectingLines();
    this.updateMarkingUI();
  }

  updateMarkingUI() {
    const inExclusion = this.markStage === 'exclusion';
    const wallComplete = this.corners.length >= 4;
    const exclusionPts = this.exclusionInProgress ? this.exclusionInProgress.length : 0;
    const hasExclusionWork = this.exclusions.length || exclusionPts;

    if (this.wallStageActions) this.wallStageActions.forEach(el => { el.style.display = inExclusion ? 'none' : ''; });
    if (this.exclusionStageActions) this.exclusionStageActions.forEach(el => { el.style.display = inExclusion ? '' : 'none'; });

    this.applyBtn.disabled = !wallComplete;
    this.undoBtn.disabled = !hasExclusionWork;
    this.resetBtn.disabled = false;
    if (this.addObjectBtn) this.addObjectBtn.disabled = !wallComplete;
    if (this.doneObjectBtn) this.doneObjectBtn.disabled = exclusionPts < 3;

    if (this.markStepDesc) {
      if (inExclusion) {
        this.markStepDesc.textContent = `Click around the object to outline it (${exclusionPts} point${exclusionPts === 1 ? '' : 's'}). Add at least 3 points, then click Done Object.`;
      } else {
        const exCount = this.exclusions.length;
        this.markStepDesc.textContent = exCount > 0
          ? `Drag the edges to fit your wall. ${exCount} object${exCount === 1 ? '' : 's'} excluded — add more or Apply.`
          : 'Drag each edge of the rectangle to fit your wall. If a couch or other object is in front, click "+ Add Object" to outline it.';
      }
    }
  }

  applyUploadVisualization() {
    if (this.corners.length < 4 || !this.productImage || !this.wallPhoto) return;
    this.panU = 0; this.panV = 0;
    this.resultCanvas.width = this.markCanvas.width;
    this.resultCanvas.height = this.markCanvas.height;
    this.rerenderUpload();
    this.goToStepByName('result');
  }

  rerenderUpload() {
    if (this.corners.length < 4 || !this.productImage || !this.wallPhoto) return;
    const ctx = this.resultCanvas.getContext('2d');
    ctx.clearRect(0, 0, this.resultCanvas.width, this.resultCanvas.height);
    ctx.drawImage(this.wallPhoto, 0, 0, this.resultCanvas.width, this.resultCanvas.height);
    this.renderPerspective(ctx);
  }

  // Result drag (upload mode)
  onResultDragStart(e) {
    if (this.stepFlow[this.currentStepIdx] !== 'result') return;
    this.isDragging = true;
    this.dragStartX = e.clientX; this.dragStartY = e.clientY;
    this.dragStartPanU = this.panU; this.dragStartPanV = this.panV;
    this.resultCanvas.setPointerCapture(e.pointerId);
    this.resultCanvas.style.cursor = 'grabbing';
  }

  onResultDragMove(e) {
    if (!this.isDragging || this.mode === 'mockup') return;
    const rect = this.resultCanvas.getBoundingClientRect();
    this.panU = this.dragStartPanU - (e.clientX - this.dragStartX) / rect.width;
    this.panV = this.dragStartPanV - (e.clientY - this.dragStartY) / rect.height;
    this.rerenderUpload();
  }

  onResultDragEnd() {
    this.isDragging = false;
    this.resultCanvas.style.cursor = 'grab';
  }

  /* ── Perspective rendering (shared) ─────────────────────────── */
  renderPerspective(ctx) {
    const corners = this.corners;
    const src = this.productImage;
    if (!src || corners.length < 4) return;
    const gridSize = 12;

    // The 4 corners define the bilinear quad for perspective mapping.
    // `this.clipPoly` (if set) defines the visible region — it can be a
    // complex multi-vertex polygon that the user traced. When unset, the
    // quad doubles as the clip.
    const clipPoly = (this.clipPoly && this.clipPoly.length >= 3) ? this.clipPoly : corners;
    const hasExclusions = this.mode === 'upload' && this.exclusions && this.exclusions.length > 0;

    // Warp the texture onto an OFFSCREEN buffer first, with normal (source-over)
    // compositing. The per-triangle overdraw in drawTriangle then makes adjacent
    // grid cells overlap harmlessly (identical texture continuation, no visible
    // seam). We composite that finished buffer onto the wall with a SINGLE
    // multiply pass — so the wall's lighting still shows through, but the grid
    // seams never get multiplied twice (which is what produced the dark "box
    // scales" grid over the mural).
    const buf = document.createElement('canvas');
    buf.width = ctx.canvas.width;
    buf.height = ctx.canvas.height;
    const bctx = buf.getContext('2d');

    const dist = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);
    const quadW = (dist(corners[0], corners[1]) + dist(corners[3], corners[2])) / 2;
    const quadH = (dist(corners[0], corners[3]) + dist(corners[1], corners[2])) / 2;
    const quadAspect = quadW / quadH;
    const imgAspect = src.naturalWidth / src.naturalHeight;

    let srcU0 = 0, srcV0 = 0, srcU1 = 1, srcV1 = 1;
    if (imgAspect > quadAspect) {
      const vis = quadAspect / imgAspect;
      srcU0 = (1 - vis) / 2; srcU1 = srcU0 + vis;
    } else {
      const vis = imgAspect / quadAspect;
      srcV0 = (1 - vis) / 2; srcV1 = srcV0 + vis;
    }

    // Apply pan
    srcU0 += this.panU; srcU1 += this.panU;
    srcV0 += this.panV; srcV1 += this.panV;
    if (srcU0 < 0) { srcU1 -= srcU0; srcU0 = 0; }
    if (srcU1 > 1) { srcU0 -= (srcU1 - 1); srcU1 = 1; }
    if (srcV0 < 0) { srcV1 -= srcV0; srcV0 = 0; }
    if (srcV1 > 1) { srcV0 -= (srcV1 - 1); srcV1 = 1; }

    for (let gy = 0; gy < gridSize; gy++) {
      for (let gx = 0; gx < gridSize; gx++) {
        const u0 = gx / gridSize, v0 = gy / gridSize;
        const u1 = (gx + 1) / gridSize, v1 = (gy + 1) / gridSize;
        const dstTL = this.bilinear(corners, u0, v0);
        const dstTR = this.bilinear(corners, u1, v0);
        const dstBL = this.bilinear(corners, u0, v1);
        const dstBR = this.bilinear(corners, u1, v1);
        const su0 = srcU0 + u0 * (srcU1 - srcU0);
        const sv0 = srcV0 + v0 * (srcV1 - srcV0);
        const su1 = srcU0 + u1 * (srcU1 - srcU0);
        const sv1 = srcV0 + v1 * (srcV1 - srcV0);
        const sx = su0 * src.naturalWidth, sy = sv0 * src.naturalHeight;
        const sw = (su1 - su0) * src.naturalWidth, sh = (sv1 - sv0) * src.naturalHeight;

        this.drawTriangle(bctx, src, sx, sy, sx + sw, sy, sx, sy + sh, dstTL.x, dstTL.y, dstTR.x, dstTR.y, dstBL.x, dstBL.y);
        this.drawTriangle(bctx, src, sx + sw, sy, sx + sw, sy + sh, sx, sy + sh, dstTR.x, dstTR.y, dstBR.x, dstBR.y, dstBL.x, dstBL.y);
      }
    }

    // Composite the finished warp onto the wall: clip to the visible polygon
    // (minus any traced exclusions), then multiply once.
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(clipPoly[0].x, clipPoly[0].y);
    for (let i = 1; i < clipPoly.length; i++) ctx.lineTo(clipPoly[i].x, clipPoly[i].y);
    ctx.closePath();
    if (hasExclusions) {
      this.exclusions.forEach(poly => {
        if (poly.length < 3) return;
        ctx.moveTo(poly[0].x, poly[0].y);
        for (let i = 1; i < poly.length; i++) ctx.lineTo(poly[i].x, poly[i].y);
        ctx.closePath();
      });
      ctx.clip('evenodd');
    } else {
      ctx.clip();
    }
    ctx.globalAlpha = this.opacity;
    ctx.globalCompositeOperation = 'multiply';
    ctx.drawImage(buf, 0, 0);
    ctx.restore();

    // ── Depth pass: ambient occlusion shadow along the top edge of the
    // wallpaper polygon, so the mural reads as inset into the wall rather
    // than floating on top of the photo. Skipped when the corner quad lies
    // outside the canvas (upload mode pre-flight).
    this.drawAmbientOcclusion(ctx, clipPoly);
  }

  /* Subtle inner shadow at the top edge of the wall polygon, simulating
     the ambient occlusion you'd see where a wall meets the ceiling. */
  drawAmbientOcclusion(ctx, clipPoly) {
    if (!clipPoly || clipPoly.length < 3) return;
    const xs = clipPoly.map(p => p.x), ys = clipPoly.map(p => p.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    const polyH = maxY - minY;
    const shadowSize = Math.min(polyH * 0.10, 36);
    if (shadowSize < 3) return;

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(clipPoly[0].x, clipPoly[0].y);
    for (let i = 1; i < clipPoly.length; i++) ctx.lineTo(clipPoly[i].x, clipPoly[i].y);
    ctx.closePath();
    ctx.clip();

    // Top shadow — ceiling line ambient occlusion
    const top = ctx.createLinearGradient(0, minY, 0, minY + shadowSize);
    top.addColorStop(0, 'rgba(0, 0, 0, 0.28)');
    top.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = top;
    ctx.fillRect(minX, minY, maxX - minX, shadowSize);

    // Subtle bottom shadow — wall-to-floor falloff (lighter than the top)
    const bot = ctx.createLinearGradient(0, maxY - shadowSize * 0.6, 0, maxY);
    bot.addColorStop(0, 'rgba(0, 0, 0, 0)');
    bot.addColorStop(1, 'rgba(0, 0, 0, 0.14)');
    ctx.fillStyle = bot;
    ctx.fillRect(minX, maxY - shadowSize * 0.6, maxX - minX, shadowSize * 0.6);

    ctx.restore();
  }

  bilinear(corners, u, v) {
    const tl = corners[0], tr = corners[1], br = corners[2], bl = corners[3];
    return {
      x: (1 - u) * (1 - v) * tl.x + u * (1 - v) * tr.x + u * v * br.x + (1 - u) * v * bl.x,
      y: (1 - u) * (1 - v) * tl.y + u * (1 - v) * tr.y + u * v * br.y + (1 - u) * v * bl.y
    };
  }

  drawTriangle(ctx, img, sx0, sy0, sx1, sy1, sx2, sy2, dx0, dy0, dx1, dy1, dx2, dy2) {
    ctx.save();
    // Expand the destination triangle ~0.5px outward from its centroid before
    // clipping. Adjacent grid cells then overlap along their shared edges, so
    // the anti-aliased hairline gaps that otherwise show up as a "box grid" over
    // the wallpaper are covered. The affine transform below is still computed
    // from the ORIGINAL vertices, so the overdrawn sliver samples the correct
    // continuation of the texture and the overlap is seamless.
    const cx = (dx0 + dx1 + dx2) / 3, cy = (dy0 + dy1 + dy2) / 3;
    const pad = 0.75;
    const ex = (x, y) => {
      const vx = x - cx, vy = y - cy;
      const d = Math.hypot(vx, vy) || 1;
      return [x + (vx / d) * pad, y + (vy / d) * pad];
    };
    const [ex0, ey0] = ex(dx0, dy0), [ex1, ey1] = ex(dx1, dy1), [ex2, ey2] = ex(dx2, dy2);
    ctx.beginPath(); ctx.moveTo(ex0, ey0); ctx.lineTo(ex1, ey1); ctx.lineTo(ex2, ey2); ctx.closePath(); ctx.clip();
    const denom = (sx0 * (sy1 - sy2) + sx1 * (sy2 - sy0) + sx2 * (sy0 - sy1));
    if (Math.abs(denom) < 1e-10) { ctx.restore(); return; }
    const m11 = (dx0 * (sy1 - sy2) + dx1 * (sy2 - sy0) + dx2 * (sy0 - sy1)) / denom;
    const m12 = (dx0 * (sx2 - sx1) + dx1 * (sx0 - sx2) + dx2 * (sx1 - sx0)) / denom;
    const m13 = (dx0 * (sx1 * sy2 - sx2 * sy1) + dx1 * (sx2 * sy0 - sx0 * sy2) + dx2 * (sx0 * sy1 - sx1 * sy0)) / denom;
    const m21 = (dy0 * (sy1 - sy2) + dy1 * (sy2 - sy0) + dy2 * (sy0 - sy1)) / denom;
    const m22 = (dy0 * (sx2 - sx1) + dy1 * (sx0 - sx2) + dy2 * (sx1 - sx0)) / denom;
    const m23 = (dy0 * (sx1 * sy2 - sx2 * sy1) + dy1 * (sx2 * sy0 - sx0 * sy2) + dy2 * (sx0 * sy1 - sx1 * sy0)) / denom;
    ctx.setTransform(m11, m21, m12, m22, m13, m23);
    ctx.drawImage(img, 0, 0);
    ctx.restore();
  }

  /* ── Download ───────────────────────────────────────────────── */
  downloadCanvas(canvas) {
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${this.productTitle || 'wall-preview'}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 'image/png');
  }
}

customElements.define('wall-visualizer', WallVisualizer);

/* ── Collection page: inject "See on my wall" buttons ─────────── */
function injectVisualizerButtons() {
  const dataEl = document.getElementById('vis-product-urls');
  if (!dataEl) return;
  let products;
  try { products = JSON.parse(dataEl.textContent); } catch (e) { return; }

  function productKey(url) {
    const m = url && url.match(/\/products\/([^/?#]+)/);
    return m ? '/products/' + m[1] : (url || '').replace(/\/$/, '').split('?')[0];
  }

  const urlMap = new Map(products.map(p => [productKey(p.url), p]));
  const processedUrls = new Set();
  const processedContainers = new Set();

  document.querySelectorAll('a[href]').forEach(link => {
    const href = productKey(link.getAttribute('href'));
    if (!urlMap.has(href)) return;
    let container = link.parentElement;
    let foundCard = false;
    for (let i = 0; i < 6; i++) {
      if (!container) break;
      const tag = container.tagName.toLowerCase();
      if (tag === 'article' || tag === 'li' || container.classList.contains('card') ||
          container.classList.contains('product-card') || container.classList.contains('product-item')) { foundCard = true; break; }
      container = container.parentElement;
    }
    if (!foundCard || !container || processedContainers.has(container) || processedUrls.has(href)) return;
    if (container.querySelector('.vis-on-wall-btn')) return; // already rendered by product-card.liquid
    processedContainers.add(container); processedUrls.add(href);

    const btn = document.createElement('a');
    btn.href = href + '?visualize=true';
    btn.className = 'vis-on-wall-btn';
    btn.setAttribute('aria-label', 'See this product on my wall');
    btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8"/><path d="M12 17v4"/><path d="m2 7 8 5 4-3 8 5"/></svg> See on my wall';
    container.appendChild(btn);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  injectVisualizerButtons();
  if (new URLSearchParams(window.location.search).get('visualize') === 'true') {
    const viz = document.querySelector('wall-visualizer');
    if (viz) setTimeout(() => viz.openModal(), 300);
  }
});
